import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import postResetPassword from 'Api/reset-password/postResetPassword';
import postResetPasswordPayload from 'Domain/reset-password/model/request/postResetPasswordRequest';
import { useResetPasswordState } from 'View/reset-password/state';
import { useLoader } from 'Store/loader';
import { useToast } from 'Store/toast';

const useResetPassword = ({ token }) => {
  const navigate = useNavigate();
  const { onOpenToast } = useToast();
  const { resetState, state } = useResetPasswordState();
  const { onShowLoader, onResetLoader } = useLoader();

  const resetPasswordMutation = useMutation({
    mutationFn: async (formData) => {
      resetState({
        isDisabled: true,
        errorMessage: '',
        successMessage: '',
      });

      onShowLoader({ variant: 'loader-primary' });

      const payload = postResetPasswordPayload(formData);
      const { data } = await postResetPassword({ payload, token });

      return { data };
    },
    onSuccess: (response) => {
      onOpenToast({
        status: 'success',
        title: 'Success',
        description: response.data?.message || 'Password has been reset successfully',
        duration: 5,
        alwaysShow: false,
      });

      resetState({
        isDisabled: false,
        isSuccess: true,
        successMessage: response.data?.message,
      });

      navigate('/login');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.errors?.[0]?.message;

      onOpenToast({
        status: 'error',
        title: 'Error',
        description: errorMessage || 'Failed to send reset password link',
        duration: 3,
        alwaysShow: false,
      });

      resetState({
        isDisabled: false,
        isSuccess: false,
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });

  return {
    ...resetPasswordMutation,
    isDisabled: state?.isDisabled,
  };
};

export default useResetPassword;
